import React, { useContext, useMemo, useState } from "react";
import { useA11y } from "../hooks/useA11y.js";
import { LangContext } from "../../context/LangContext.js";
import { CURSOR_SIZES, TEXT_SIZES } from "../a11y.types.js";
import { ToolbarCard } from "./ToolbarCard.jsx";
import { ToolbarSection } from "./ToolbarSection.jsx";
import {
  GlobeIcon,
  FocusIcon,
  LinkIcon,
  DyslexiaIcon,
  SpacingIcon,
  LineHeightIcon,
  WeightIcon,
  DarkContrastIcon,
  LightContrastIcon,
  HighContrastIcon,
  HighSatIcon,
  LowSatIcon,
  MonochromeIcon,
  ReadingGuideIcon,
  StopIcon,
  CursorIcon,
  MaskIcon,
  HideImageIcon,
  KeyboardIcon,
  ResetIcon,
} from "./icons.jsx";

export function A11yToolbar({ onClose, onOpenHelp }) {
  const a11y = useA11y();
  const lang = useContext(LangContext);
  const nep = !!(lang && lang.state && lang.state.np);
  const dispatchLang = lang && lang.dispatch;
  const s = a11y.settings;
  const [statusMsg, setStatusMsg] = useState("");

  const t = (en, np) => (nep ? np : en);

  const onReset = () => {
    a11y.reset();
    setStatusMsg(
      t("Accessibility settings reset", "पहुँचयोग्यता सेटिङहरू रिसेट गरियो"),
    );
  };

  const dec = () => {
    const i = TEXT_SIZES.indexOf(s.textSize);
    const next = TEXT_SIZES[Math.max(0, i - 1)];
    a11y.set("textSize", next);
  };
  const inc = () => {
    const i = TEXT_SIZES.indexOf(s.textSize);
    const next = TEXT_SIZES[Math.min(TEXT_SIZES.length - 1, i + 1)];
    a11y.set("textSize", next);
  };

  const cycleCursor = () => {
    const i = CURSOR_SIZES.indexOf(s.cursorSize);
    const next = CURSOR_SIZES[(i + 1) % CURSOR_SIZES.length];
    a11y.set("cursorSize", next);
  };

  const sectionIds = useMemo(
    () => ({
      lang: "a11y-sec-lang",
      content: "a11y-sec-content",
      color: "a11y-sec-color",
      tools: "a11y-sec-tools",
      reset: "a11y-sec-reset",
    }),
    [],
  );

  return (
    <div className="a11y-toolbar">
      <div className="a11y-toolbar__row" style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className="a11y-toolbar__help"
          onClick={onOpenHelp}
        >
          {t("Keyboard shortcuts (Alt+H)", "किबोर्ड शर्टकट (Alt+H)")}
        </button>
      </div>

      <ToolbarSection
        id={sectionIds.lang}
        title="Language"
        nepTitle="भाषा"
        nep={nep}
      >
        <ToolbarCard
          icon={<GlobeIcon />}
          label="English"
          pressed={!nep}
          onClick={() => dispatchLang && dispatchLang({ type: "ENGLISH" })}
        />
        <ToolbarCard
          icon={<GlobeIcon />}
          label="नेपाली"
          pressed={nep}
          onClick={() => dispatchLang && dispatchLang({ type: "NEPALI" })}
        />
      </ToolbarSection>

      <ToolbarSection
        id={sectionIds.content}
        title="Content"
        nepTitle="सामग्री"
        nep={nep}
      >
        <div
          className="a11y-stepper"
          role="group"
          aria-label={t("Font size", "पाठ आकार")}
        >
          <button
            type="button"
            className="a11y-stepper__btn"
            onClick={dec}
            disabled={s.textSize === TEXT_SIZES[0]}
            aria-label={t("Decrease text size", "पाठको आकार घटाउनुहोस्")}
          >
            −
          </button>
          <span className="a11y-stepper__value" aria-live="polite">
            {s.textSize}%
          </span>
          <button
            type="button"
            className="a11y-stepper__btn"
            onClick={inc}
            disabled={s.textSize === TEXT_SIZES[TEXT_SIZES.length - 1]}
            aria-label={t("Increase text size", "पाठको आकार बढाउनुहोस्")}
          >
            +
          </button>
        </div>
        <ToolbarCard
          icon={<FocusIcon />}
          label={t("Highlight focus", "फोकस हाइलाइट")}
          pressed={s.highlightFocus}
          onClick={() => a11y.toggle("highlightFocus")}
        />
        <ToolbarCard
          icon={<LinkIcon />}
          label={t("Highlight links", "लिङ्क हाइलाइट")}
          pressed={s.underlineLinks}
          onClick={() => a11y.toggle("underlineLinks")}
        />
        <ToolbarCard
          icon={<DyslexiaIcon />}
          label={t("Dyslexia font", "डिस्लेक्सिया फन्ट")}
          pressed={s.dyslexiaFont}
          onClick={() => a11y.toggle("dyslexiaFont")}
        />
        <ToolbarCard
          icon={<SpacingIcon />}
          label={t("Letter spacing", "अक्षर दूरी")}
          pressed={s.textSpacing}
          onClick={() => a11y.toggle("textSpacing")}
        />
        <ToolbarCard
          icon={<LineHeightIcon />}
          label={t("Line height", "लाइन उचाइ")}
          pressed={s.textSpacing}
          onClick={() => a11y.toggle("textSpacing")}
        />
        {/* <ToolbarCard
          icon={<WeightIcon />}
          label={t('Font weight', 'फन्ट वजन')}
          disabled
          badge={t('Soon', 'छिट्टै')}
        /> */}
      </ToolbarSection>

      <ToolbarSection
        id={sectionIds.color}
        title="Colors"
        nepTitle="रंग"
        nep={nep}
      >
        <ToolbarCard
          icon={<DarkContrastIcon />}
          label={t("Dark contrast", "अँध्यारो कन्ट्रास्ट")}
          pressed={s.contrast === "dark"}
          onClick={() => a11y.set("contrast", "dark")}
          ariaLabel={t(
            "Dark contrast (color setting)",
            "अँध्यारो कन्ट्रास्ट (रंग सेटिङ)",
          )}
        />
        <ToolbarCard
          icon={<LightContrastIcon />}
          label={t("Light contrast", "उज्यालो कन्ट्रास्ट")}
          pressed={s.contrast === "light"}
          onClick={() => a11y.set("contrast", "light")}
          ariaLabel={t(
            "Light contrast (color setting)",
            "उज्यालो कन्ट्रास्ट (रंग सेटिङ)",
          )}
        />
        <ToolbarCard
          icon={<HighContrastIcon />}
          label={t("High contrast", "उच्च कन्ट्रास्ट")}
          pressed={s.contrast === "high"}
          onClick={() => a11y.set("contrast", "high")}
          ariaLabel={t(
            "High contrast (color setting)",
            "उच्च कन्ट्रास्ट (रंग सेटिङ)",
          )}
        />
        <ToolbarCard
          icon={<HighSatIcon />}
          label={t("High saturation", "उच्च संतृप्ति")}
          pressed={s.saturation === "high-saturation"}
          onClick={() => a11y.set("saturation", "high-saturation")}
        />
        <ToolbarCard
          icon={<LowSatIcon />}
          label={t("Low saturation", "कम संतृप्ति")}
          pressed={s.saturation === "grayscale"}
          onClick={() => a11y.set("saturation", "grayscale")}
        />
        <ToolbarCard
          icon={<MonochromeIcon />}
          label={t("Monochrome", "मोनोक्रोम")}
          pressed={s.saturation === "monochrome"}
          onClick={() => a11y.set("saturation", "monochrome")}
        />
      </ToolbarSection>

      <ToolbarSection
        id={sectionIds.tools}
        title="Tools"
        nepTitle="उपकरणहरू"
        nep={nep}
      >
        <ToolbarCard
          icon={<ReadingGuideIcon />}
          label={t("Reading guide", "पढाइ गाइड")}
          pressed={s.readingGuide}
          onClick={() => a11y.toggle("readingGuide")}
        />
        <ToolbarCard
          icon={<StopIcon />}
          label={t("Stop animations", "एनिमेसन रोक्नुहोस्")}
          pressed={s.pauseAnimations}
          onClick={() => a11y.toggle("pauseAnimations")}
        />
        <ToolbarCard
          icon={<CursorIcon />}
          label={t("Big cursor", "ठूलो कर्सर")}
          pressed={s.cursorSize !== 1}
          onClick={cycleCursor}
          ariaLabel={`${t("Big cursor", "ठूलो कर्सर")} (${s.cursorSize}×)`}
        />
        {/* <ToolbarCard
          icon={<MaskIcon />}
          label={t('Reading mask', 'पढाइ मास्क')}
          disabled
          badge={t('Soon', 'छिट्टै')}
        />
        <ToolbarCard
          icon={<HideImageIcon />}
          label={t('Hide images', 'तस्वीर लुकाउनुहोस्')}
          disabled
          badge={t('Soon', 'छिट्टै')}
        />
        <ToolbarCard
          icon={<KeyboardIcon />}
          label={t('Keyboard navigation', 'किबोर्ड नेभिगेसन')}
          disabled
          badge={t('Soon', 'छिट्टै')}
        /> */}
      </ToolbarSection>

      <ToolbarSection
        id={sectionIds.reset}
        title="Reset"
        nepTitle="रिसेट"
        nep={nep}
      >
        <ToolbarCard
          icon={<ResetIcon />}
          label={t("Reset all settings", "सबै सेटिङहरू रिसेट गर्नुहोस्")}
          onClick={onReset}
        />
      </ToolbarSection>

      <div className="a11y-sr-only" role="status" aria-live="polite">
        {statusMsg}
      </div>
    </div>
  );
}

export default A11yToolbar;
